import { ConvertHelper } from '@barthauer/abc/core';
import { ErrorHandlingService } from '@barthauer/abc/core/error-handling';
import Dexie from 'dexie';
import { Observable, Subject, Subscription } from 'rxjs';
import { CacheDataSet } from './cache.model';

export interface OnlineService {
  isOnline: boolean;
  isOnlineChanged: Observable<boolean>;
  connectionType: string;
  connectionTypeChanged: Observable<string>;
  removeEventListening();
}

class BrowserOnlineService implements OnlineService {
  private readonly isOnlineChangedSubject = new Subject<boolean>();
  private readonly connectionTypeChangedSubject = new Subject<string>();

  public get isOnline(): boolean {
    return navigator.onLine;
  }

  public get isOnlineChanged(): Observable<boolean> {
    return this.isOnlineChangedSubject.asObservable();
  }

  public get connectionType(): string {
    return this.getCurrentConnectionType();
  }

  public get connectionTypeChanged(): Observable<string> {
    return this.connectionTypeChangedSubject.asObservable();
  }

  private getCurrentConnectionType(): string {
    return (navigator as any).connection?.type ?? (navigator as any).connection?.effectiveType;
  }

  //attention: "this" of the eventhandler is in vs.code-debugger wrong,
  //in chrome not, its "myselfe" like intended
  private handleOnline = () => this.isOnlineChangedSubject.next(true);
  private handleOffline = () => this.isOnlineChangedSubject.next(false);
  private handleConnectionTypeChanged = () => this.connectionTypeChangedSubject.next(this.getCurrentConnectionType());

  constructor() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    const conn = (navigator as any).connection;
    //(conn.onchange !== undefined) check important in cordova environments
    //conn.onchange in browser is normally null
    if (conn && conn.onchange !== undefined) {
      conn.addEventListener('change', this.handleConnectionTypeChanged);
    }
  }

  public removeEventListening(): void {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    const conn = (navigator as any).connection;
    if (conn && conn.onchange !== undefined) {
      conn.removeEventListener('change', this.handleConnectionTypeChanged);
    }
  }
}

export interface DataEntry {
  key: string;
  value: any;
}

export class CacheStorageAdapter {
  private static readonly isOnlineChangedSubject = new Subject<boolean>();
  private static readonly isDownloadCompleteChangedSubject = new Subject<boolean>();
  private static readonly isDataChangedSubject = new Subject<void>();
  private static readonly isOfflineChangedSubject = new Subject<boolean>();
  private static lastIsOffline;
  private static wasServerSlow: boolean;

  private static raiseOnlineChanged(online: boolean) {
    this.isOnlineChangedSubject.next(online);
    this.raiseDataChanged();
    this.checkOfflineChanged();
  }

  private static raiseDownloadCompleteChanged(complete: boolean) {
    this.isDownloadCompleteChangedSubject.next(complete);
    this.raiseDataChanged();
  }

  private static raiseIsOfflineChanged(value: boolean) {
    this.isOfflineChangedSubject.next(value);
  }

  public static get isOffline(): boolean {
    return !this.isOnline || this.forceOfflineMode;
  }

  private static checkOfflineChanged() {
    const isOffline = this.isOffline;
    if (this.lastIsOffline != isOffline) {
      this.lastIsOffline = isOffline;
      this.raiseIsOfflineChanged(isOffline);
    }
  }

  public static raiseDataChanged() {
    this.isDataChangedSubject.next(null);
  }

  public static get isOnlineChanged(): Observable<boolean> {
    return this.isOnlineChangedSubject.asObservable();
  }

  public static get isDownloadCompleteChanged(): Observable<boolean> {
    return this.isDownloadCompleteChangedSubject.asObservable();
  }

  public static get isDataChanged(): Observable<void> {
    return this.isDataChangedSubject.asObservable();
  }

  public static get isOfflineChanged(): Observable<boolean> {
    return this.isOfflineChangedSubject.asObservable();
  }

  public static get isOnline(): boolean {
    return this.onlineService.isOnline;
  }

  private static readonly connectionTypeChangedSubject = new Subject<string>();

  static raiseConnectionTypeChanged(type: string) {
    this.connectionTypeChangedSubject.next(type);
  }

  public static get connectionTypeChanged(): Observable<string> {
    return this.connectionTypeChangedSubject.asObservable();
  }

  public static get connectionType(): string {
    return this.onlineService.connectionType;
  }

  public static get useOfflineMode(): boolean {
    if (this.isOnlineForced) {
      return false;
    } else {
      this.forceOnlineModeUntil = null;
    }
    return (
      this.forceOfflineMode ||
      (this.isDownloadComplete && (!this.isOnline || !this.isServerAvailable || this.isServerSlow))
    );
  }

  private static get isOnlineForced(): boolean {
    return this.forceOnlineModeUntil && Date.now() <= this.forceOnlineModeUntil;
  }

  private static forceOfflineModeKey_: string;
  private static get forceOfflineModeKey(): string {
    if (!this.forceOfflineModeKey_) {
      this.forceOfflineModeKey_ = 'forceOfflineMode_' + this.offlineStorageKeySuffix;
    }
    return this.forceOfflineModeKey_;
  }

  public static set forceOfflineMode(value: boolean) {
    if (value !== this.forceOfflineMode) {
      localStorage.setItem(this.forceOfflineModeKey, value ? 'true' : 'false');
      this.raiseDataChanged();
      this.checkOfflineChanged();
      if (value) {
        this.stopUpdateServer();
      } else {
        this.startUpdateServer();
      }
    }
  }
  public static get forceOfflineMode(): boolean {
    return this.isDownloadComplete && localStorage.getItem(this.forceOfflineModeKey) === 'true';
  }

  private static forceOnlineModeUntil: number;
  public static forceOnlineMode(seconds: number): void {
    this.forceOnlineModeUntil = Date.now() + seconds * 1000;
  }

  private static isServerSlow_: boolean;
  private static set isServerSlow(value: boolean) {
    const offline = this.useOfflineMode;
    if (this.isServerSlow_ !== value) {
      this.isServerSlow_ = value;
      if (value) {
        this.wasServerSlow = true;
      }
      setTimeout(() => {
        if (offline !== this.useOfflineMode) {
          this.useOfflineModeChangedSubject.next(this.useOfflineMode);
        }
      }, 3000);
    }
  }

  private static defaultWaitForOnlineModeIntervalSeconds_: number;
  public static get defaultWaitForOnlineModeIntervalSeconds(): number {
    return this.defaultWaitForOnlineModeIntervalSeconds_;
  }

  public setDefaultWaitForOnlineModeIntervalSeconds(value: number) {
    if (!value || value < 10) {
      value = 10;
    }
    CacheStorageAdapter.defaultWaitForOnlineModeIntervalSeconds_ = value;

    CacheStorageAdapter.stopUpdateServer();
    CacheStorageAdapter.lastUpdateServerAvailableIntervalHandler = () => {
      if (CacheStorageAdapter.isDownloadComplete) {
        this.updateServerAvailable();
      }
    };
    CacheStorageAdapter.startUpdateServer();
  }

  private static lastUpdateServerAvailableIntervalId: number;
  private static lastUpdateServerAvailableIntervalHandler: TimerHandler;
  private static startUpdateServer() {
    this.stopUpdateServer();
    if (this.lastUpdateServerAvailableIntervalHandler) {
      setTimeout(this.lastUpdateServerAvailableIntervalHandler, 10);
      CacheStorageAdapter.lastUpdateServerAvailableIntervalId = setInterval(
        this.lastUpdateServerAvailableIntervalHandler,
        CacheStorageAdapter.defaultWaitForOnlineModeIntervalSeconds * 1000
      );
    }
  }

  private static stopUpdateServer() {
    if (CacheStorageAdapter.lastUpdateServerAvailableIntervalId) {
      clearInterval(CacheStorageAdapter.lastUpdateServerAvailableIntervalId);
      CacheStorageAdapter.lastUpdateServerAvailableIntervalId = null;
    }
  }

  private static get isServerSlow(): boolean {
    return this.isServerSlow_ || this.onlineService_?.connectionType === '2g';
  }

  private static onlineService_: OnlineService;
  public static get onlineService(): OnlineService {
    if (this.onlineService_ == null) {
      this.onlineService = new BrowserOnlineService();
    }
    return this.onlineService_;
  }

  private static isOnlineChangedSubscription: Subscription;
  private static connectionTypeChangedSubscription: Subscription;
  public static set onlineService(value: OnlineService) {
    this.onlineService_ = value;

    const wasOnline = this.onlineService_?.isOnline;

    this.isOnlineChangedSubscription?.unsubscribe();
    this.isOnlineChangedSubscription = this.onlineService.isOnlineChanged.subscribe((_) => {
      this.raiseOnlineChanged(_);
    });

    if (wasOnline !== this.isOnline) {
      this.raiseOnlineChanged(this.isOnline);
    }

    const origConnectionType = this.onlineService_?.connectionType;

    this.connectionTypeChangedSubscription?.unsubscribe();
    this.connectionTypeChangedSubscription = this.onlineService.connectionTypeChanged.subscribe((_) => {
      this.raiseConnectionTypeChanged(_);
    });

    if (origConnectionType !== this.connectionType) {
      this.raiseConnectionTypeChanged(this.connectionType);
    }
  }

  private static isServerAvailable_: boolean;
  private static readonly useOfflineModeChangedSubject = new Subject<boolean>();

  public static get useOfflineModeChanged(): Observable<boolean> {
    return this.useOfflineModeChangedSubject.asObservable();
  }

  public static get isServerAvailable(): boolean {
    return this.isServerAvailable_;
  }
  public static set isServerAvailable(value: boolean) {
    const offline = this.useOfflineMode;
    if (this.isServerAvailable_ !== value) {
      this.isServerAvailable_ = value;
      if (offline !== this.useOfflineMode) {
        this.useOfflineModeChangedSubject.next(this.useOfflineMode);
      }
    }
  }

  private static get offlineStorageKeySuffix(): string {
    let baseURI = window.document?.head?.baseURI.split('?')[0].split('#')[0];
    let pos = baseURI.indexOf('/index.htm');
    if (pos >= 0) {
      baseURI = baseURI.substring(0, pos);
    }
    pos = baseURI.indexOf('(');
    if (pos >= 0) {
      baseURI = baseURI.substring(0, pos);
    }
    while (baseURI.endsWith('/')) {
      baseURI = baseURI.substring(0, baseURI.length - 1);
    }
    return baseURI + '_' + (window.document?.title ?? window.document?.head?.title)?.replace(' ', '');
  }

  private static localStorageDownloadCompleteKey_: string;
  private static get localStorageDownloadCompleteKey(): string {
    if (!this.localStorageDownloadCompleteKey_) {
      this.localStorageDownloadCompleteKey_ = 'offlineCacheDownloadComplete_' + this.offlineStorageKeySuffix;
    }
    return this.localStorageDownloadCompleteKey_;
  }

  public static notifyDownloadStarted(): void {
    localStorage.removeItem(this.localStorageDownloadCompleteKey);
    this.stopUpdateServer();
    this.raiseDownloadCompleteChanged(false);
    this.wasServerSlow = false;
  }

  public static notifyDownloadComplete(): void {
    this.forceOnlineMode(10);
    localStorage.setItem(this.localStorageDownloadCompleteKey, 'true');
    this.startUpdateServer();
    this.raiseDownloadCompleteChanged(true);
    this.wasServerSlow = false;
  }

  public static notifyDownloadCancelled(): void {
    this.startUpdateServer();
  }

  public static get isDownloadComplete(): boolean {
    return ConvertHelper.toBoolean(localStorage.getItem(this.localStorageDownloadCompleteKey));
  }

  public items: CacheDataSet[];
  private currentContextKey: string;
  private currentDataSets: Map<string, CacheStorageDataSet> = new Map<string, CacheStorageDataSet>();

  constructor(
    private getContextKey: () => Promise<string>,
    private checkServerAvailable: () => Promise<{ available: boolean; timeout: boolean }>,
    public error: ErrorHandlingService
  ) {
    this.items = new Array<CacheDataSet>();

    CacheStorageAdapter.isOnlineChanged.subscribe((online) => {
      if (online) {
        CacheStorageAdapter.wasServerSlow = false;
        this.updateServerAvailable();
      } else {
        CacheStorageAdapter.isServerAvailable = false;
      }
    });

    CacheStorageAdapter.connectionTypeChanged.subscribe((type) => {
      this.updateServerAvailable();
    });

    var forceInit = CacheStorageAdapter.onlineService;

    CacheStorageAdapter.lastIsOffline = CacheStorageAdapter.isOffline;
  }

  public updateServerAvailable() {
    if (this.checkServerAvailable && !CacheStorageAdapter.forceOfflineMode) {
      if (!CacheStorageAdapter.isOnlineForced) {
        if (CacheStorageAdapter.isServerAvailable) {
          CacheStorageAdapter.isServerSlow = CacheStorageAdapter.wasServerSlow; //pessimistic
        }
        this.checkServerAvailable().then((_) => {
          CacheStorageAdapter.isServerAvailable = _.available;
          CacheStorageAdapter.isServerSlow = _.timeout;
        });
      }
    }
  }

  public onServerUnavailable(action: () => void) {
    if (this.checkServerAvailable && !CacheStorageAdapter.isServerAvailable) {
      this.checkServerAvailable().then((_) => {
        CacheStorageAdapter.isServerSlow = _.timeout;
        if (!_.available) {
          CacheStorageAdapter.isServerAvailable = false;
          action();
        } else {
          CacheStorageAdapter.isServerAvailable = true;
        }
      });
    }
  }

  public async waitForOnlineMode(action: () => Promise<void>, intervalMilliseconds?: number): Promise<void> {
    let wait = true;
    try {
      if (!CacheStorageAdapter.useOfflineMode) {
        await action();
        wait = false;
      }
    } catch {}
    if (wait && this.checkServerAvailable) {
      if (CacheStorageAdapter.isServerAvailable) {
        CacheStorageAdapter.isServerSlow = CacheStorageAdapter.wasServerSlow; //pessimistic
      }
      this.checkServerAvailable()
        .then(async (_) => {
          let retry = true;
          CacheStorageAdapter.isServerSlow = _.timeout;
          CacheStorageAdapter.isServerAvailable = _.available;
          if (!CacheStorageAdapter.useOfflineMode) {
            try {
              await action();
              retry = false;
            } catch (err) {
              console.warn(err);
            }
          }
          if (retry) {
            setTimeout(async () => {
              await this.waitForOnlineMode(action, intervalMilliseconds);
            }, intervalMilliseconds ?? 1000 * (CacheStorageAdapter.defaultWaitForOnlineModeIntervalSeconds ?? 10));
          }
        })
        .catch((err) => {
          console.warn(err);
          setTimeout(async () => {
            await this.waitForOnlineMode(action, intervalMilliseconds);
          }, intervalMilliseconds ?? 1000 * (CacheStorageAdapter.defaultWaitForOnlineModeIntervalSeconds ?? 10));
        });
    }
  }

  public async estimateCacheUtilization(): Promise<{
    used: number;
    quota: number;
  }> {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      if (estimate.quota) {
        return { used: estimate.usage / estimate.quota, quota: estimate.quota };
      }
    }
    return undefined;
  }

  public getCacheableItems(): CacheDataSet[] {
    let arr: CacheDataSet[] = [];
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      if (item.cacheMethod) {
        arr.push(item);
      }
    }
    return arr;
  }

  public async clear(): Promise<void> {
    for (const item of this.items) {
      this.deleteStorageDataSet(item);
    }
    CacheStorageAdapter.notifyDownloadStarted();
  }

  private async getStorageDataSets(): Promise<Map<string, CacheStorageDataSet>> {
    if (this.getContextKey) {
      let key = await this.getContextKey();
      if ((key ?? '') === '') {
        key = localStorage.getItem('CacheContextKey') ?? '';
      } else {
        localStorage.setItem('CacheContextKey', key);
      }
      if (key != this.currentContextKey) {
        this.currentDataSets.clear();
        this.currentContextKey = key;
      }
    }
    return this.currentDataSets;
  }

  public async getStorageDataSet(dataSet: CacheDataSet): Promise<CacheStorageDataSet> {
    const dataSets = await this.getStorageDataSets();
    let storage = dataSets.get(dataSet.name);
    if (!storage) {
      storage = await CacheStorageDataSet.get(dataSet, this.currentContextKey);
      if (storage) {
        dataSets.set(dataSet.name, storage);
      }
    }
    return storage;
  }

  private async deleteStorageDataSet(dataSet: CacheDataSet): Promise<boolean> {
    let storage = await this.getStorageDataSet(dataSet);
    if (storage) {
      await storage.delete();
      const dataSets = await this.getStorageDataSets();
      dataSets.delete(dataSet.name);
      return true;
    }
    return false;
  }

  public async createStorageDataSet(dataSet: CacheDataSet, metadata?: object): Promise<CacheStorageDataSet> {
    const storageItems = await this.getStorageDataSets();
    let storage = storageItems.get(dataSet.name);
    if (storage && !(await storage.tryUpdate(dataSet.tableNames, metadata))) {
      this.deleteStorageDataSet(dataSet);
      storage = null;
    }
    if (!storage) {
      storage = await CacheStorageDataSet.create(dataSet, this.currentContextKey, metadata);
      if (storage) {
        storageItems.set(dataSet.name, storage);
      }
    }
    return storage;
  }

  public async ensureStorageDataSet(dataSet: CacheDataSet, metadata?: object): Promise<CacheStorageDataSet> {
    var result = await this.getStorageDataSet(dataSet);
    if (!result) {
      result = await this.createStorageDataSet(dataSet, metadata);
    }
    return result;
  }

  public async isInCurrentContext(storageDataSet: CacheStorageDataSet): Promise<boolean> {
    return this.getContextKey && (await this.isInContext(storageDataSet, await this.getContextKey()));
  }

  public async isInContext(storageDataSet: CacheStorageDataSet, contextKey: string): Promise<boolean> {
    if (!storageDataSet) {
      return false;
    }
    const key = await storageDataSet.contextKey;
    return key === contextKey;
  }
}

export class CacheStorageDataSet {
  private static readonly metaDataTableName: string = '#metadata';
  private static readonly keyColumnName: string = 'key';
  private static readonly timestampColumnName: string = 'timestamp';
  private static readonly valueColumnName: string = 'value';
  private static readonly metaDataTablesKey: string = '#tables';
  private static readonly schemaVersion = 1;
  public static readonly autoIncrementKeyTablePrefix: string = '++';

  private db_: Promise<{ db: Dexie; key: string }>;
  private dbinstance: { db: Dexie; key: string };

  private static countLimitSavingRetry: number = 20;
  private static countRetry: number = 0;

  private constructor(private dbFactory: () => Promise<{ db: Dexie; key: string }>) {}

  private get db(): Promise<Dexie> {
    const db = this.dbinstance?.db;
    if (db) {
      return Promise.resolve(db);
    }
    return (this.db_ ?? (this.db_ = this.dbFactory())).then((_) => {
      this.dbinstance = _;
      return _?.db;
    });
  }

  public get contextKey(): Promise<string> {
    const key = this.dbinstance?.key;
    if (key) {
      return Promise.resolve(key);
    }
    return (this.db_ ?? (this.db_ = this.dbFactory())).then((_) => {
      this.dbinstance = _;
      return _?.key;
    });
  }

  static async isValid(dbFactory: Promise<Dexie>): Promise<boolean> {
    try {
      const db = await dbFactory;
      if (!db.isOpen()) await db.open();
      return true;
    } catch {
      return false;
    }
  }

  private async opendb(): Promise<Dexie> {
    const db = await (this.db_ ?? (this.db_ = this.dbFactory()));
    this.dbinstance = db;
    if (db?.db?.isOpen() === false) {
      await db.db.open();
    }
    return db?.db;
  }

  static async loadMetaDataCore(db: Dexie): Promise<[string[], { [key: string]: any }]> {
    let items = await db.table(CacheStorageDataSet.metaDataTableName).toArray();
    if (items.length === 0) {
      return null;
    }
    let tableNames: string[] = null;
    const metadata = {};
    for (let item of items) {
      const key: string = item[CacheStorageDataSet.keyColumnName];
      const value: any = item[CacheStorageDataSet.valueColumnName];
      if (key === CacheStorageDataSet.metaDataTablesKey) {
        tableNames = JSON.parse(value);
      } else {
        metadata[key] = value;
      }
    }
    return [tableNames, metadata];
  }

  public static async create(
    dataSet: CacheDataSet,
    contextKey: string,
    metadata: object
  ): Promise<CacheStorageDataSet> {
    try {
      const name = this.getName(dataSet, contextKey);
      return new CacheStorageDataSet(() =>
        CacheStorageDataSet.getDb(name, dataSet.tableNames, metadata).then((db) => {
          return { db: db, key: contextKey };
        })
      );
    } catch {
      return null;
    }
  }

  private static async getDb(name: string, tableNames: string[], metadata: object): Promise<Dexie> {
    let db = new Dexie(name);
    const schema = this.createSchema(tableNames);
    db.version(this.schemaVersion).stores(schema);
    if (!(await this.checkTables(db, tableNames))) {
      db.close();
      db = null;
      try {
        await Dexie.delete(name);
      } catch {}
      db = new Dexie(name);
      db.version(this.schemaVersion).stores(schema);
    }
    if (metadata) {
      await this.saveMetaDataCore(db, tableNames, metadata);
    }
    return db;
  }

  public static async get(dataSet: CacheDataSet, contextKey: string): Promise<CacheStorageDataSet> {
    let dbnames = await this.getDatabaseNames();
    if (dbnames) {
      const name = this.getName(dataSet, contextKey);
      if (dbnames.findIndex((_) => _ === name) >= 0) {
        let db = new Dexie(name);
        const schema = this.createSchema(dataSet.tableNames);
        db.version(this.schemaVersion).stores(schema);
        if ((await this.isValid(Promise.resolve(db))) && (await this.checkTables(db, dataSet.tableNames))) {
          return new CacheStorageDataSet(() =>
            CacheStorageDataSet.getDb(name, dataSet.tableNames, null).then((db) => {
              return { db: db, key: contextKey };
            })
          );
        }
      }
    }
    return undefined;
  }

  private static async getDatabaseNames(): Promise<string[]> {
    try {
      return await Dexie.getDatabaseNames();
    } catch {
      return null;
    }
  }

  public static getName(dataSet: CacheDataSet, contextKey: string): string {
    if (contextKey == null) {
      return dataSet.name;
    }
    return dataSet.name + '?' + contextKey;
  }

  public async loadMetaData(): Promise<{ [key: string]: any }> {
    const db = await this.opendb();
    if (db) {
      const metaData = await CacheStorageDataSet.loadMetaDataCore(db);
      if (metaData?.length > 1) {
        return metaData[1];
      }
    }
    return undefined;
  }

  public async tryUpdate(tableNames: string[], metadata: object): Promise<boolean> {
    try {
      if (!(await CacheStorageDataSet.checkTables(await this.opendb(), tableNames))) {
        return false;
      }
      await CacheStorageDataSet.saveMetaDataCore(await this.opendb(), tableNames, metadata);
      return true;
    } catch {
      return false;
    }
  }

  private static async checkTables(db: Dexie, tableNames: string[]): Promise<boolean> {
    try {
      tableNames = this.ensureTableNames(tableNames);
      const metadata = await this.loadMetaDataCore(db);
      if (!metadata) {
        return true;
      }
      const currentTableNames = metadata[0];
      if (!currentTableNames) {
        return false;
      }
      for (let name of tableNames) {
        if (currentTableNames.findIndex((_) => _ === name) < 0) {
          return false;
        }
      }
      return true;
    } catch {
      return false;
    }
  }

  private static ensureTableNames(tableNames: string[]): string[] {
    if (!tableNames || tableNames.length === 0) {
      tableNames = [this.getDataTableName(null)];
    }
    return tableNames;
  }

  public async delete(): Promise<void> {
    const db = await this.db;
    if (db.isOpen()) {
      db.close();
    }
    const name = db.name;
    this.db_ = null;
    this.dbinstance = null;
    await Dexie.delete(name);
  }

  public async set(key: string, value: any, tableName?: string): Promise<void> {
    await CacheStorageDataSet.saveData(await this.opendb(), CacheStorageDataSet.getDataTableName(tableName), [
      { key: key, value: value },
    ]);
  }

  public async setData(data: DataEntry[], tableName?: string): Promise<void> {
    await CacheStorageDataSet.saveData(await this.opendb(), CacheStorageDataSet.getDataTableName(tableName), data);
  }

  public async mergeArrayData(data: DataEntry[], tableName?: string): Promise<void> {
    await CacheStorageDataSet.saveData(
      await this.opendb(),
      CacheStorageDataSet.getDataTableName(tableName),
      data,
      true
    );
  }

  public async appendValue(value: any, tableName?: string): Promise<number> {
    return await CacheStorageDataSet.saveData(await this.opendb(), CacheStorageDataSet.getDataTableName(tableName), [
      { key: null, value: value },
    ])[0];
  }

  public async appendValues(values: any[], tableName?: string): Promise<any[]> {
    const arr: DataEntry[] = [];
    for (let v of values) {
      arr.push({ key: null, value: v });
    }
    return await CacheStorageDataSet.saveData(
      await this.opendb(),
      CacheStorageDataSet.getDataTableName(tableName),
      values
    );
  }

  public async processAllValues(
    action: (value: any) => Promise<any>,
    deleteAfterProcess: boolean,
    tableName?: string
  ): Promise<any[]> {
    return this.processAllEntries((key, value) => action(value), deleteAfterProcess, tableName);
  }

  public async processAllEntries(
    action: (key: string, value: any, timestamp: number) => Promise<any>,
    deleteAfterProcess: boolean,
    tableName?: string
  ): Promise<any[]> {
    const dataTableName = CacheStorageDataSet.getDataTableName(tableName);
    const db = await this.opendb();
    const result: any[] = [];
    const rows = await db.transaction('r', dataTableName, (_) => _.table(dataTableName).toArray());
    for (const r of rows) {
      const e: DataEntry = r;
      result.push(await action(e.key, e.value, r[CacheStorageDataSet.timestampColumnName]));
      if (deleteAfterProcess) {
        await db.transaction('rw', dataTableName, (_) => _.table(dataTableName).delete(e.key));
      }
    }
    return result;
  }

  public async hasValues(tableName?: string): Promise<boolean> {
    const dataTableName = CacheStorageDataSet.getDataTableName(tableName);
    const db = await this.opendb();
    return (await db.transaction('r', dataTableName, (_) => _.table(dataTableName).count())) > 0;
  }

  public async get(key: string, tableName?: string, timeoutMinutes?: number): Promise<any> {
    return (
      await CacheStorageDataSet.loadData(
        await this.opendb(),
        CacheStorageDataSet.getDataTableName(tableName),
        [key],
        timeoutMinutes
      )
    )[0];
  }

  public async getData(keys: string[], tableName?: string, timeoutMinutes?: number): Promise<any[]> {
    return await CacheStorageDataSet.loadData(
      await this.opendb(),
      CacheStorageDataSet.getDataTableName(tableName),
      keys,
      timeoutMinutes
    );
  }

  private static getDataTableName(tableName: string): string {
    if (!tableName || tableName == '') {
      return 'data';
    }
    if (tableName.startsWith(CacheStorageDataSet.autoIncrementKeyTablePrefix)) {
      return tableName.substring(CacheStorageDataSet.autoIncrementKeyTablePrefix.length);
    } else {
      return tableName;
    }
  }

  static async saveMetaDataCore(db: Dexie, tableNames: string[], metadata: object): Promise<void> {
    const tableName = this.metaDataTableName;
    await db.transaction('rw', tableName, async (_) => await _.table(tableName).clear());
    if (metadata) {
      for (let key of Object.keys(metadata)) {
        const v = metadata[key];
        await this.saveData(db, tableName, [{ key: key, value: v }]);
      }
    }
    await this.saveData(db, tableName, [
      {
        key: this.metaDataTablesKey,
        value: JSON.stringify(this.ensureTableNames(tableNames)),
      },
    ]);
  }

  private static async saveData(
    db: Dexie,
    tableName: string,
    data: DataEntry[],
    mergeArrays?: boolean
  ): Promise<any[]> {
    try {
      return await db.transaction('rw', tableName, async (_) => {
        const timestamp = Date.now();
        const values: any[] = [];
        const entriesToPut = [];
        const entriesToDelete = [];

        let arrayValues: Map<string, unknown[]>;
        if (mergeArrays) {
          const keys: string[] = [];
          for (let e of data) {
            if (e.key) {
              keys.push(e.key);
            }
          }
          const entries = await _.table(tableName).bulkGet(keys);
          arrayValues = new Map<string, unknown[]>();
          for (let i = 0; i < keys.length; i++) {
            const e = entries[i];
            if (Array.isArray(e?.value)) {
              arrayValues.set(e.key, e.value);
            }
          }
        }

        for (let e of data) {
          if (e.value) {
            const entry = {};
            let value = e.value;
            if (e.key) {
              entry[this.keyColumnName] = e.key;
              const current = arrayValues?.get(e.key);
              if (current && Array.isArray(value)) {
                current.push(...value);
                arrayValues.set(e.key, current);
                value = current;
              }
            }

            entry[this.valueColumnName] = value;
            entry[this.timestampColumnName] = timestamp;

            if (e.key) {
              entriesToPut.push(entry);
              values.push(e.key);
            } else {
              values.push(await _.table(tableName).put(entry));
            }
          } else {
            if (e.key) {
              entriesToDelete.push(e.key);
            }
            values.push(null);
          }
        }

        if (entriesToPut && entriesToPut.length > 0) {
          await _.table(tableName).bulkPut(entriesToPut);
          CacheStorageDataSet.countRetry = 0;
        }

        if (entriesToDelete && entriesToDelete.length > 0) {
          await _.table(tableName).bulkDelete(entriesToDelete);
        }

        return values;
      });
    } catch (err) {
      CacheStorageDataSet.countRetry++;
      if (CacheStorageDataSet.countRetry <= CacheStorageDataSet.countLimitSavingRetry) {
        return await CacheStorageDataSet.saveData(db, tableName, data);
      } else {
        CacheStorageDataSet.countRetry = 0;
        throw err;
      }
    }
  }

  private static async loadData(db: Dexie, tableName: string, keys: string[], timeoutMinutes: number): Promise<any[]> {
    return await db.transaction('r', tableName, async (_) => {
      const result: any[] = [];
      for (const key of keys) {
        const entry = await _.table(tableName).get(key);
        if (
          !entry ||
          (timeoutMinutes && this.diffMinutes(entry[this.timestampColumnName], Date.now()) < timeoutMinutes)
        ) {
          result.push(null);
        } else {
          result.push(entry[this.valueColumnName]);
        }
      }
      return result;
    });
  }

  static diffMinutes(timestamp1: number, timestamp2: number): number {
    return (timestamp2 - timestamp1) / 60000;
  }

  static createSchema(tableNames: string[]): { [tableName: string]: string } {
    const entry = {};
    entry[this.metaDataTableName] = this.keyColumnName;
    for (let tableName of this.ensureTableNames(tableNames)) {
      if (tableName.startsWith(CacheStorageDataSet.autoIncrementKeyTablePrefix)) {
        entry[tableName.substring(CacheStorageDataSet.autoIncrementKeyTablePrefix.length)] = '++' + this.keyColumnName;
      } else {
        entry[tableName] = this.keyColumnName;
      }
    }
    return entry;
  }

  public async cleanup(tableName?: string, timeoutMinutes?: number): Promise<void> {
    const toDelete: string[] = [];
    const now = Date.now();
    await this.processAllEntries(
      async (key, value, timestamp) => {
        if (timeoutMinutes && CacheStorageDataSet.diffMinutes(timestamp, now) > timeoutMinutes) {
          toDelete.push(key);
        }
      },
      false,
      tableName
    );
    if (toDelete.length) {
      await this.setData(
        toDelete.map((key) => {
          return { key: key, value: null };
        }),
        tableName
      );
    }
  }
}
